import { useState, useRef, useEffect } from 'react';
import { cn } from '../lib/utils';
import { ChevronDown, X } from 'lucide-react';

export interface MultiSelectProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  id?: string;
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Selecione...',
  error,
  helperText,
  disabled,
  id,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectId = id || label.toLowerCase().replace(/\s+/g, '-');

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const remove = (value: string) => {
    onChange(selected.filter((v) => v !== value));
  };

  const selectedLabels = options
    .filter((o) => selected.includes(o.value));

  return (
    <div className="w-full" ref={containerRef}>
      <label
        htmlFor={selectId}
        className="block text-sm font-medium text-foreground mb-1"
      >
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          id={selectId}
          disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
          className={cn(
            'flex w-full min-h-[38px] rounded-md border bg-background px-3 py-2 text-sm text-left',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
            'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
            'transition-colors items-center gap-1 flex-wrap text-foreground',
            error ? 'border-red-500 focus:ring-red-500' : 'border-input',
            open && 'ring-2 ring-primary border-transparent'
          )}
        >
          {open ? (
            <span className="text-muted-foreground">
              {selectedLabels.length > 0
                ? `${selectedLabels.length} selecionado(s)`
                : placeholder}
            </span>
          ) : selectedLabels.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            selectedLabels.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium"
              >
                {opt.label}
                {!disabled && selectedLabels.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remover ${opt.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(opt.value);
                    }}
                    className="hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))
          )}
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground ml-auto shrink-0 transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>

        {open && (
          <div
            role="listbox"
            className="absolute z-50 mt-1 w-full max-h-[200px] overflow-auto rounded-md border border-border bg-card shadow-lg"
          >
            {options.map((opt) => {
              const isSelected = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent text-sm text-foreground',
                    isSelected && 'bg-primary/10'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(opt.value)}
                    className="rounded border-input text-primary focus:ring-primary"
                  />
                  {opt.label}
                </label>
              );
            })}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Nenhuma opção disponível
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
          <span className="w-3 h-3 flex items-center justify-center">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}
